import { createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { queryKeys, sharedApi } from "@fieldcheck/shared";
import { api, createApiQuery, SERVER_BASE_URL } from "@/lib/api";
import { isTauriRuntime } from "@/lib/platform";
import { useI18n } from "@/providers/i18n-provider";

interface SocialAuthButtonsProps {
  /** Ruta relativa segura a la que volver tras autenticar (p.ej. "/"). */
  redirect?: string;
  /** Texto contextual: "continue" (login) o "signup" (registro). */
  mode?: "login" | "signup";
}

type ProviderId = "google" | "apple";

const PROVIDERS_STALE_TIME_MS = 5 * 60 * 1000;

function startUrl(provider: ProviderId, redirect: string | undefined) {
  const base = `${SERVER_BASE_URL.replace(/\/$/u, "")}/api/auth/sign-in/social/${provider}`;
  if (!redirect) {
    return base;
  }
  // oxlint-disable-next-line lingui/no-unlocalized-strings -- query string, no texto de UI
  return `${base}?redirect=${encodeURIComponent(redirect)}`;
}

/**
 * Botones de inicio de sesión social. Solo muestra los providers HABILITADOS
 * (derivados de la presencia de credenciales en el backend). Si ninguno está
 * habilitado, no renderiza nada y el formulario email/password sigue intacto.
 */
export function SocialAuthButtons(props: SocialAuthButtonsProps) {
  const { t } = useI18n();

  // P23/CC-60 · encargo A (el PUENTE de la caché). Este `queryFn` escribía
  // `data ?? {google:false, apple:false}` sobre `openapi-fetch`, que NUNCA
  // lanza: con el backend caído resolvía en SUCCESS con un objeto FABRICADO.
  // Y como comparte `queryKey` (`auth.socialProviders`) y `staleTime` con
  // `profile/connected-accounts-section`, y el LOGIN/REGISTRO se monta ANTES,
  // dejaba ese `success` inventado en la caché COMPARTIDA: la sección del
  // perfil lo leía fresco, no refetcheaba y NUNCA llegaba a `isError` — su
  // propio arreglo quedaba PUENTEADO desde aquí.
  // `createApiQuery` → `handleApiResponse`, que lanza en cualquier no-2xx: lo
  // que queda escrito en la caché es un ERROR, no un dato inventado.
  // El degradado visible no cambia: `enabled()` sigue vacío si la query no está
  // en `success`, así que I4 (el formulario de login no espera a providers)
  // sigue en pie.
  const providers = createApiQuery(
    () => queryKeys.auth.socialProviders(),
    async () => sharedApi.socialProviders(api),
    () => ({
      // El estado de providers cambia raramente; cachéalo de forma agresiva.
      staleTime: PROVIDERS_STALE_TIME_MS,
      retry: false,
    }),
  );

  const enabled = createMemo<ProviderId[]>(() => {
    // P23/I2+I4: sin esta guarda, `providers.data` suspende y el FORMULARIO de
    // login entero espera a `/api/auth/social/providers` — un endpoint que no
    // pinta ni un campo del formulario.
    if (!providers.isSuccess) {
      return [];
    }
    const { data } = providers;
    if (!data) {
      return [];
    }
    const list: ProviderId[] = [];
    if (data.google) {
      list.push("google");
    }
    if (data.apple) {
      list.push("apple");
    }
    return list;
  });

  /**
   * Native (Tauri): OAuth MUST run in the SYSTEM browser — Google blocks OAuth
   * inside embedded WebViews. Intercept the click, prevent the in-WebView
   * navigation, and open the provider start URL externally; the server returns
   * via an App/Universal Link handled by `DeepLinkingProvider`. On web this is a
   * no-op and the plain `<a>` navigation runs as usual.
   */
  const handleClick = (event: MouseEvent, provider: ProviderId) => {
    if (!isTauriRuntime()) {
      return;
    }
    event.preventDefault();
    void (async () => {
      // Dynamic import keeps `@tauri-apps/plugin-opener` out of the web bundle.
      const { openUrl } = await import("@tauri-apps/plugin-opener");
      await openUrl(startUrl(provider, props.redirect));
    })();
  };

  const label = (provider: ProviderId) => {
    const verb = props.mode === "signup" ? t(msg`Sign up with`) : t(msg`Continue with`);
    const name = provider === "google" ? "Google" : "Apple";
    return `${verb} ${name}`;
  };

  return (
    <Show when={enabled().length > 0}>
      <div class="space-y-3">
        <div class="relative flex items-center py-1">
          <div class="flex-grow border-t border-border" />
          <span class="mx-3 shrink-0 text-xs text-muted-foreground">{t(msg`or`)}</span>
          <div class="flex-grow border-t border-border" />
        </div>

        <For each={enabled()}>
          {(provider) => (
            <a
              href={startUrl(provider, props.redirect)}
              onClick={(event) => {
                handleClick(event, provider);
              }}
              class="flex h-11 w-full items-center justify-center gap-3 rounded-xl border border-border bg-background text-sm font-medium text-foreground transition-[transform,background-color] hover:bg-muted/60 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid={`social-${provider}`}
            >
              <Show when={provider === "google"} fallback={<AppleIcon />}>
                <GoogleIcon />
              </Show>
              <span>{label(provider)}</span>
            </a>
          )}
        </For>
      </div>
    </Show>
  );
}

function GoogleIcon() {
  return (
    <svg class="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg class="h-5 w-5 fill-current" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M16.36 1.43c0 1.14-.42 2.22-1.18 3.05-.83.92-2.17 1.62-3.32 1.53-.13-1.1.43-2.27 1.16-3.07.82-.9 2.27-1.6 3.34-1.51ZM20.5 17.2c-.6 1.38-.88 2-1.65 3.22-1.08 1.7-2.6 3.82-4.48 3.84-1.67.01-2.1-1.1-4.37-1.08-2.27.01-2.74 1.1-4.41 1.08-1.88-.02-3.32-1.93-4.4-3.63C-1.32 16.43-1.62 9.62 1.42 6.03c1.08-1.28 2.78-2.09 4.38-2.09 1.63 0 2.66 1.1 4.01 1.1 1.31 0 2.11-1.1 4-1.1 1.43 0 2.95.78 4.03 2.13-3.54 1.94-2.96 7-1.34 9.13Z" />
    </svg>
  );
}
